import { breakpoints, useHooks } from '@everlywell/leaves';
import { Icon, theme } from '@everlywell/ui-kit';
import { Basket } from '@phosphor-icons/react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import React, { useState, useEffect, useRef } from 'react';
import { stripePublicKey } from 'utils/helpers/paymentHelpers';

import { useCart } from './Cart.hooks';
import * as S from './Cart.styles';

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
// const stripePromise = loadStripe(stripePublicKey);
/**
 * Cart is the experience of showing the user what is in their order as a floating
 * side window
 */
const Cart = () => {
  const { openCart, isCartOpen, order } = useCart();

  const currentOrder = order;
  const lineItems = order?.line_items ?? [];

  const cartTotal = {
    totalPrice: currentOrder ? +currentOrder.total : 0,
    subTotal: currentOrder ? +currentOrder.amount : 0,
    productQuantity:
      currentOrder?.line_items?.reduce((acc, item) => acc + item.quantity, 0) ||
      0,
    currencyId: 'USD',
    currencyFormat: '$',
  };

  const isOpen = isCartOpen;
  const openFloatCart = openCart;

  const checkoutRef = useRef<HTMLAnchorElement>(null);
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const { width } = useHooks.useWindowSize();
  const isCartFullscreen = width && width <= breakpoints.forTabletHorizontalUp;
  useHooks.useDisableScroll(isOpen && !!isCartFullscreen);

  useEffect(() => {
    setHasLoaded(true);
  }, [stripePromise]);

  useEffect(() => {
    if (isOpen && !stripePromise) setStripePromise(loadStripe(stripePublicKey));
    if (checkoutRef?.current) {
      isOpen ? checkoutRef.current.focus() : checkoutRef.current.blur();
    }
  }, [isOpen, stripePromise]);

  if (!hasLoaded) {
    return (
      <S.OpenCartBtn
        tabIndex={-1}
        data-testid="loading-cart"
        aria-label="Loading Cart"
      >
        <S.CartInfoWrapper>
          <Icon
            pointerEvents={'none'}
            as={Basket}
            color={theme.colors.tints.uiGrey}
          />
        </S.CartInfoWrapper>
      </S.OpenCartBtn>
    );
  }

  return order ? (
    <>
      <S.OpenCartBtn
        tabIndex={-1}
        data-testid="open-cart"
        aria-label="Open Cart"
        onClick={() => openFloatCart()}
      >
        {cartTotal ? (
          <S.CartInfoWrapper tabIndex={0}>
            {/* New DLS CSS Override */}
            <Icon as={Basket} color={theme.colors.tints.black} />
            {lineItems.length > 0 ? (
              <S.CartQuantity data-testid="cart-quantity">
                {cartTotal.productQuantity}
              </S.CartQuantity>
            ) : null}
          </S.CartInfoWrapper>
        ) : null}
      </S.OpenCartBtn>
    </>
  ) : (
    <S.OpenCartBtn
      tabIndex={-1}
      data-testid="loading-cart"
      aria-label="Loading Cart"
    >
      <S.CartInfoWrapper>
        <Icon
          pointerEvents={'none'}
          as={Basket}
          color={theme.colors.tints.uiGrey}
        />
      </S.CartInfoWrapper>
    </S.OpenCartBtn>
  );
};

export { Cart };

export default Cart;
